import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Zap } from 'lucide-react';
import type { Api, Credential, SessionInfo } from '../core/api';
import { prefs } from '../core/prefs';
import { t, uiLang } from '../core/i18n';
import { PermissionBroker, type PermissionRequest } from '../core/permissions';
import { platform } from '../core/platform';
import type { Download, MediaKind, ServerMsg, Tab } from '../core/protocol';
import { RemoteSession, type ConnState, type StreamMode } from '../core/session';
import { originOf } from '../core/url';
import { BottomBar, TabSheet, TabStrip, Toolbar } from './Chrome';
import { DownloadsBar, FileChooserPanel, JsDialog, NewTabPage, PermissionPrompt, Toast, type DialogReq, type RecentSite } from './Overlays';
import { PasswordFillBar, PasswordSaveBar, type SavePrompt } from './PasswordUi';
import { SettingsPage } from './SettingsPage';
import { SiteInfo } from './SiteInfo';
import { Viewport } from './Viewport';

interface Props {
  api: Api;
  onLogout: () => void;
  onDirectMode?: () => void;
  initialUrl?: string;
}

const dprNow = () => Math.min(2, Math.max(1, window.devicePixelRatio || 1));
const RECENT_KEY = 'gcb.recent';

function loadRecent(): RecentSite[] {
  try {
    return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]');
  } catch {
    return [];
  }
}

function useNarrow() {
  const mq = useMemo(() => matchMedia('(max-width: 640px)'), []);
  const [narrow, setNarrow] = useState(mq.matches);
  useEffect(() => {
    const h = () => setNarrow(mq.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, [mq]);
  return narrow;
}

export function BrowserView({ api, onLogout, onDirectMode, initialUrl }: Props) {
  const [info, setInfo] = useState<SessionInfo | null>(null);
  const [startError, setStartError] = useState('');
  const [conn, setConn] = useState<ConnState>('connecting');
  const [everConnected, setEverConnected] = useState(false);
  const [mode, setMode] = useState<StreamMode>('webrtc');
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [active, setActive] = useState('');
  const [remote, setRemote] = useState({ width: 1280, height: 720 });
  const [cursor, setCursor] = useState('default');
  const [stream, setStream] = useState<MediaStream>();
  const [perms, setPerms] = useState<PermissionRequest[]>([]);
  const [chooser, setChooser] = useState<{ id: number; multiple: boolean; accept: string; origin: string } | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [downloads, setDownloads] = useState<Download[]>([]);
  const [dialog, setDialog] = useState<DialogReq | null>(null);
  const [toast, setToast] = useState<{ text: string; onClick?: () => void } | null>(null);
  const [pwSave, setPwSave] = useState<SavePrompt | null>(null);
  const [pwFill, setPwFill] = useState<{ origin: string; tabId: string; creds: Credential[] } | null>(null);
  const [muted, setMuted] = useState(true);
  const [activeMedia, setActiveMedia] = useState<MediaKind[]>([]);
  const [settings, setSettings] = useState(false);
  const [tabSheet, setTabSheet] = useState(false);
  const [focusAddr, setFocusAddr] = useState(0);
  const [kbReq, setKbReq] = useState(0);
  const [recent, setRecent] = useState<RecentSite[]>(loadRecent);
  const sessionRef = useRef<RemoteSession | undefined>(undefined);
  const frameSink = useRef<((b64: string) => void) | null>(null);
  const pendingClip = useRef<string | null>(null);
  const userMuted = useRef(false);
  const pendingUrl = useRef(initialUrl);
  const narrow = useNarrow();

  const refreshMedia = useCallback(() => {
    const s = sessionRef.current;
    setActiveMedia((['camera', 'microphone', 'screen'] as MediaKind[]).filter((k) => s?.hasLocalTrack(k)));
  }, []);
  const broker = useMemo(() => new PermissionBroker(api, () => sessionRef.current, refreshMedia), [api, refreshMedia]);

  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    if (conn === 'connected') setEverConnected(true);
  }, [conn]);

  // Remember recently visited sites for the new tab page (this device only).
  useEffect(() => {
    const done = tabs.filter((x) => !x.loading && /^https?:/.test(x.url) && x.title);
    if (!done.length) return;
    setRecent((prev) => {
      let next = [...prev];
      for (const x of done) {
        next = next.filter((r) => r.url !== x.url);
        next.unshift({ url: x.url, title: x.title, favicon: x.favicon });
      }
      next = next.slice(0, 8);
      if (JSON.stringify(next) === JSON.stringify(prev)) return prev;
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      } catch {
        /* storage unavailable */
      }
      return next;
    });
  }, [tabs]);

  const handleMsg = useCallback(
    (m: ServerMsg) => {
      const s = sessionRef.current;
      switch (m.t) {
        case 'tabs':
          setTabs(m.tabs);
          setActive(m.active);
          if (pendingUrl.current && s && m.active) {
            s.send({ t: 'navigate', tabId: m.active, url: pendingUrl.current });
            pendingUrl.current = undefined;
          }
          break;
        case 'viewport':
          setRemote({ width: m.width, height: m.height });
          break;
        case 'cursor':
          setCursor(m.cursor);
          break;
        case 'permissionRequest': {
          const req: PermissionRequest = { id: m.id, origin: m.origin, kinds: m.kinds, tabId: m.tabId };
          const remembered = broker.remembered_(req);
          if (remembered === 'deny') broker.decide(req, false, false);
          else if (remembered === 'allow') {
            broker.decide(req, true, false).then((r) => {
              if (!r.ok) setPerms((p) => [...p, { ...req, id: -req.id }]);
            });
          } else setPerms((p) => [...p, req]);
          break;
        }
        case 'mediaStop':
          broker.stop(m.kind);
          break;
        case 'notification':
          platform().notify({
            title: m.title,
            body: m.body,
            icon: m.icon,
            tag: m.tag,
            onClick: () => s?.send({ t: 'notificationEvent', id: m.id, event: 'click' }),
          });
          break;
        case 'clipboard':
          navigator.clipboard?.writeText(m.text).catch(() => {
            // Safari/iOS need a user gesture: copy on the next tap/keypress.
            pendingClip.current = m.text;
            setToast({ text: t('copied') });
          });
          break;
        case 'fileChooser': {
          const tab = tabsRef.current.find((x) => x.id === activeRef.current) ?? tabsRef.current[0];
          setChooser({ id: m.id, multiple: m.multiple, accept: m.accept, origin: originOf(tab?.url ?? '') });
          setUploadProgress(null);
          break;
        }
        case 'download':
          setDownloads((list) => {
            const i = list.findIndex((d) => d.guid === m.download.guid);
            const next = i >= 0 ? list.map((d, j) => (j === i ? m.download : d)) : [...list, m.download];
            return next.slice(-6);
          });
          if (m.download.state === 'completed' && sessionRef.current) {
            platform()
              .saveDownload(api, sessionRef.current.info.id, m.download.guid, m.download.filename)
              .catch(() => {});
          }
          break;
        case 'dialog':
          setDialog({ id: m.id, type: m.type, message: m.message, defaultPrompt: m.defaultPrompt, origin: m.origin });
          break;
        case 'pwSave': {
          if (!prefs.offerSave()) break;
          void (async () => {
            const r = await api.passwords(m.origin).catch(() => null);
            if (!r || r.neverSave === true) return;
            const same = r.passwords.find((c) => c.username === m.username);
            if (same && (await api.revealPassword(same.id).catch(() => null)) === m.password) return; // nothing new
            setPwSave({ origin: m.origin, username: m.username, password: m.password, update: !!same });
          })();
          break;
        }
        case 'pwField': {
          if (!prefs.offerFill()) break;
          void (async () => {
            const r = await api.passwords(m.origin).catch(() => null);
            if (r && r.passwords.length) setPwFill({ origin: m.origin, tabId: m.tabId, creds: r.passwords });
          })();
          break;
        }
        case 'crashed':
          setToast({ text: t('crashed') });
          break;
      }
    },
    [api, broker],
  );
  const handleRef = useRef(handleMsg);
  handleRef.current = handleMsg;

  // --- start / resume the cloud session
  const start = useCallback(async () => {
    setStartError('');
    const host = document.querySelector('.viewport-host') as HTMLElement | null;
    const width = Math.floor(host?.clientWidth || window.innerWidth);
    const height = Math.floor(host?.clientHeight || window.innerHeight - 80);
    const create = () => api.createSession({ width, height, dpr: dprNow(), lang: navigator.language || uiLang });
    try {
      const [si, saved] = await Promise.all([create(), broker.load()]);
      setInfo(si);
      const forceScreencast = new URLSearchParams(location.search).has('screencast');
      const s = new RemoteSession(
        api,
        si,
        {
          onMessage: (m) => handleRef.current(m),
          onState: setConn,
          onMode: setMode,
          onStream: (st) => setStream(st),
          onFrame: (b64) => frameSink.current?.(b64),
        },
        create,
        forceScreencast,
      );
      s.setSitePermissions(saved.map((p) => ({ origin: p.origin, kind: p.kind, allow: p.allow })));
      sessionRef.current = s;
      (window as any).__gcbSession = s; // handle for diagnostics / e2e tests
      setMode(s.mode);
      s.connect(width, height);
    } catch (e: any) {
      setStartError(e?.message || t('errorGeneric'));
    }
  }, [api, broker]);

  useEffect(() => {
    start();
    const off = platform().onResume(() => sessionRef.current?.reconnectNow());
    return () => {
      off();
      sessionRef.current?.close();
      broker.dispose();
    };
  }, [start, broker]);

  const s = sessionRef.current;
  const activeTab = tabs.find((x) => x.id === active);
  const blank = !activeTab || !activeTab.url || activeTab.url === 'about:blank';
  const overlayOpen = !!dialog || settings || tabSheet;

  const unmute = () => {
    userMuted.current = false;
    setMuted(false);
  };
  const onGesture = useCallback(() => {
    if (pendingClip.current !== null) {
      const text = pendingClip.current;
      pendingClip.current = null;
      navigator.clipboard?.writeText(text).catch(() => {});
    }
    if (muted && !userMuted.current) setMuted(false);
  }, [muted]);

  const navigate = (url: string) => {
    if (!activeTab) s?.send({ t: 'newTab', url });
    else s?.send({ t: 'navigate', tabId: activeTab.id, url });
  };
  const newTab = () => {
    s?.send({ t: 'newTab' });
    setFocusAddr((n) => n + 1);
  };

  const onShortcut = (sc: string) => {
    if (!s) return;
    const idx = tabs.findIndex((x) => x.id === active);
    switch (sc) {
      case 'newTab':
        newTab();
        break;
      case 'closeTab':
        if (active) s.send({ t: 'closeTab', tabId: active });
        break;
      case 'focusAddress':
        setFocusAddr((n) => n + 1);
        break;
      case 'reload':
        s.send({ t: 'reload', tabId: active });
        break;
      case 'back':
        s.send({ t: 'back', tabId: active });
        break;
      case 'forward':
        s.send({ t: 'forward', tabId: active });
        break;
      case 'nextTab':
      case 'prevTab':
        if (tabs.length > 1) {
          const n = tabs[(idx + (sc === 'nextTab' ? 1 : -1) + tabs.length) % tabs.length];
          s.send({ t: 'activateTab', tabId: n.id });
        }
        break;
    }
  };

  const toolbarProps = {
    tab: activeTab,
    focusSignal: focusAddr,
    muted,
    activeMedia,
    onNavigate: navigate,
    onBack: () => s?.send({ t: 'back', tabId: active }),
    onForward: () => s?.send({ t: 'forward', tabId: active }),
    onReload: () => s?.send({ t: 'reload', tabId: active }),
    onStop: () => s?.send({ t: 'stop', tabId: active }),
    onToggleMute: () => {
      userMuted.current = !muted;
      setMuted(!muted);
    },
    onStopMedia: (k: MediaKind) => broker.stop(k),
    onSettings: () => setSettings(true),
    onLogout,
    extraItems: onDirectMode ? [{ label: t('directMode'), icon: Zap, onClick: onDirectMode }] : undefined,
    siteInfo: (
      <SiteInfo api={api} broker={broker} url={activeTab?.url ?? ''} canClearCookies onSettings={() => setSettings(true)} onToast={(text) => setToast({ text })} up={narrow} />
    ),
  };

  const connecting = !startError && conn !== 'replaced' && (!info || !everConnected);
  const showBanner = everConnected && conn === 'reconnecting';

  return (
    <div className="app">
      {!narrow && (
        <header className="chrome">
          <TabStrip
            tabs={tabs}
            active={active}
            muted={muted && !blank && conn === 'connected' && mode === 'webrtc'}
            onActivate={(id) => s?.send({ t: 'activateTab', tabId: id })}
            onClose={(id) => s?.send({ t: 'closeTab', tabId: id })}
            onNew={newTab}
            onUnmute={unmute}
          />
          <Toolbar {...toolbarProps} />
        </header>
      )}
      {narrow && <div style={{ height: 'var(--safe-top)', background: 'var(--bar)', flex: 'none' }} />}

      <main className="viewport-host">
        <Viewport
          session={s}
          remote={remote}
          cursor={cursor}
          mode={mode}
          stream={stream}
          frameSink={frameSink}
          inputEnabled={conn === 'connected' && !blank && !overlayOpen}
          muted={muted}
          keyboardRequest={kbReq}
          onResize={(w, h) => sessionRef.current?.resize(w, h)}
          onShortcut={onShortcut}
          onGesture={onGesture}
        />
        {blank && everConnected && <NewTabPage recent={recent} onNavigate={navigate} />}

        {connecting && (
          <div className="blank-state">
            <span className="spinner" />
            {!info ? t('starting') : t('connecting')}
          </div>
        )}
        {startError && (
          <div className="blank-state column">
            <span>{startError}</span>
            <button className="btn" onClick={start}>
              {t('retry')}
            </button>
          </div>
        )}
        {conn === 'replaced' && (
          <div className="blank-state column">
            <span>{t('replaced')}</span>
            <button className="btn primary" onClick={() => location.reload()}>
              {t('takeOver')}
            </button>
          </div>
        )}
        {showBanner && (
          <div className="banner">
            <span className="spinner" />
            {t('connectionLost')}
          </div>
        )}
        {mode === 'screencast' && conn === 'connected' && <div className="mode-note">{t('fallback')}</div>}

        {perms[0] && (
          <PermissionPrompt
            key={perms[0].id}
            req={{ ...perms[0], id: Math.abs(perms[0].id) }}
            onDecide={async (allow, remember) => {
              const req = perms[0];
              const r = await broker.decide({ ...req, id: Math.abs(req.id) }, allow, remember);
              setPerms((p) => p.filter((x) => x.id !== req.id));
              if (allow && !r.ok) setToast({ text: t('localDenied') });
            }}
          />
        )}
        {!perms[0] && chooser && (
          <FileChooserPanel
            origin={chooser.origin}
            multiple={chooser.multiple}
            accept={chooser.accept}
            progress={uploadProgress}
            onCancel={() => {
              s?.send({ t: 'fileChooserCancel', id: chooser.id });
              setChooser(null);
            }}
            onFiles={async (files) => {
              if (!s) return;
              setUploadProgress(0);
              try {
                await api.upload(s.info.id, chooser.id, files, setUploadProgress);
              } catch {
                setToast({ text: t('errorGeneric') });
              }
              setChooser(null);
              setUploadProgress(null);
            }}
          />
        )}
        {pwSave && (
          <PasswordSaveBar
            prompt={pwSave}
            onClose={() => setPwSave(null)}
            onSave={() => {
              const x = pwSave;
              setPwSave(null);
              api.savePassword(x.origin, x.username, x.password).then(() => setToast({ text: t('pwSaved') }), () => setToast({ text: t('errorGeneric') }));
            }}
            onNever={() => {
              const x = pwSave;
              setPwSave(null);
              api.setNeverSave(x.origin, true).catch(() => {});
            }}
          />
        )}
        {!pwSave && pwFill && (
          <PasswordFillBar
            origin={pwFill.origin}
            creds={pwFill.creds}
            onClose={() => setPwFill(null)}
            onFill={async (cred) => {
              const x = pwFill;
              const password = await api.revealPassword(cred.id).catch(() => '');
              if (password) s?.send({ t: 'pwFill', tabId: x.tabId, username: cred.username, password });
              setPwFill(null);
            }}
          />
        )}
        {toast && (
          <Toast
            text={toast.text}
            onClick={pendingClip.current !== null || toast.onClick ? () => (onGesture(), toast.onClick?.(), setToast(null)) : undefined}
            onClose={() => setToast(null)}
          />
        )}
      </main>

      <DownloadsBar items={downloads} onSave={(d) => s && platform().saveDownload(api, s.info.id, d.guid, d.filename)} onClear={() => setDownloads([])} />

      {narrow && <BottomBar {...toolbarProps} tabCount={tabs.length} onTabs={() => setTabSheet(true)} onKeyboard={() => setKbReq((n) => n + 1)} />}
      {tabSheet && (
        <TabSheet
          tabs={tabs}
          active={active}
          onActivate={(id) => s?.send({ t: 'activateTab', tabId: id })}
          onClose={(id) => s?.send({ t: 'closeTab', tabId: id })}
          onNew={newTab}
          onDone={() => setTabSheet(false)}
        />
      )}

      {dialog && (
        <JsDialog
          d={dialog}
          onClose={(accept, text) => {
            s?.send({ t: 'dialogResponse', id: dialog.id, accept, text });
            setDialog(null);
          }}
        />
      )}
      {settings && (
        <SettingsPage
          api={api}
          broker={broker}
          onClose={() => setSettings(false)}
          onEndSession={async () => {
            if (info) await api.endSession(info.id).catch(() => {});
            sessionRef.current?.close();
            location.reload();
          }}
          onLogout={onLogout}
        />
      )}
    </div>
  );
}
